import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, MailCheck, KeyRound } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { PoweredBy } from "@/components/Footer";

const inp = "w-full rounded-lg bg-[#070912] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-[#3ad7ff]/60";

const Shell = ({ icon: Icon, title, sub, children, testid }) => (
  <div className="relative min-h-screen grid place-items-center px-5 py-16 bg-[#04050a]" data-testid={testid}>
    <PoweredBy />
    <div className="w-full max-w-md cx-card cx-brackets rounded-2xl p-8 sm:p-10 cx-rise">
      <Link to="/"><img src="/logo-wide.png" alt="CLIP-EX" className="h-9 mb-8" /></Link>
      <div className="w-11 h-11 rounded-xl grid place-items-center bg-[#2f7bff]/20 mb-5"><Icon className="w-5 h-5 text-[#3ad7ff]" /></div>
      <h1 className="font-display text-2xl font-bold text-white">{title}</h1>
      <p className="text-sm text-slate-400 mt-2 mb-7">{sub}</p>
      {children}
    </div>
  </div>
);

const Err = ({ msg }) => msg ? <div className="text-sm text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2" data-testid="auth-error">{msg}</div> : null;

export function ForgotPassword() {
  const { t } = useI18n();
  const [f, setF] = useState({ workspace_id: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await api.post("/auth/forgot-password", f); setSent(true); } catch (x) { setErr(formatApiError(x)); } finally { setBusy(false); }
  };
  if (sent) return (
    <Shell icon={MailCheck} title={t("fp.sent_t")} sub={t("fp.sent")} testid="forgot-sent">
      <Link to="/login" className="cx-ghost rounded-full w-full py-3 text-sm text-white flex justify-center" data-testid="forgot-back-link">{t("fp.back")}</Link>
    </Shell>
  );
  return (
    <Shell icon={KeyRound} title={t("fp.title")} sub={t("fp.sub")} testid="forgot-page">
      <form onSubmit={submit} className="space-y-4">
        <input required placeholder={t("auth.ws_id")} value={f.workspace_id} onChange={(e) => setF({ ...f, workspace_id: e.target.value })} className={inp} data-testid="forgot-workspace-input" />
        <input required type="email" placeholder="toi@clip-ex.ca" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inp} data-testid="forgot-email-input" />
        <Err msg={err} />
        <button disabled={busy} className="cx-btn rounded-full w-full py-3 text-sm font-semibold flex items-center justify-center gap-2" data-testid="forgot-submit-btn">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}{t("fp.send")}
        </button>
        <Link to="/login" className="block text-center text-xs text-slate-500 hover:text-white" data-testid="forgot-back-link">{t("fp.back")}</Link>
      </form>
    </Shell>
  );
}

export function ResetPassword() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState({ a: "", b: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (pw.a !== pw.b) { setErr(t("rp.mismatch")); return; }
    setBusy(true); setErr("");
    try { await api.post("/auth/reset-password", { token, password: pw.a }); setDone(true); } catch (x) { setErr(formatApiError(x)); } finally { setBusy(false); }
  };
  if (done) return (
    <Shell icon={MailCheck} title={t("rp.title")} sub={t("rp.done")} testid="reset-done">
      <Link to="/login" className="cx-btn rounded-full w-full py-3 text-sm font-semibold flex justify-center" data-testid="reset-login-link">{t("fp.back")}</Link>
    </Shell>
  );
  return (
    <Shell icon={KeyRound} title={t("rp.title")} sub={t("rp.sub")} testid="reset-page">
      <form onSubmit={submit} className="space-y-4">
        <input required type="password" minLength={8} placeholder={t("rp.pw")} value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} className={inp} data-testid="reset-password-input" />
        <input required type="password" minLength={8} placeholder={t("rp.pw2")} value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} className={inp} data-testid="reset-password-confirm-input" />
        <Err msg={token.length < 20 ? t("rp.invalid") : err} />
        <button disabled={busy || token.length < 20} className="cx-btn rounded-full w-full py-3 text-sm font-semibold flex items-center justify-center gap-2" data-testid="reset-submit-btn">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}{t("rp.save")}
        </button>
      </form>
    </Shell>
  );
}
